import type { SceneLayerKind, SceneLayerRecord, SceneLayerRegistry } from './types';

const MAX_EXTENDS_DEPTH = 8;

export function resolveLayerFromRegistry(
  registry: SceneLayerRegistry,
  kind: SceneLayerKind,
  id: string,
): SceneLayerRecord {
  return resolveLayerInner(registry, kind, id, [], 0);
}

function resolveLayerInner(
  registry: SceneLayerRegistry,
  kind: SceneLayerKind,
  id: string,
  stack: string[],
  depth: number,
): SceneLayerRecord {
  if (depth > MAX_EXTENDS_DEPTH) {
    throw new Error(`Layer extends depth exceeded ${MAX_EXTENDS_DEPTH}: ${[...stack, id].join(' -> ')}`);
  }
  if (stack.includes(id)) {
    throw new Error(`Layer extends cycle detected: ${[...stack, id].join(' -> ')}`);
  }

  const layer = registry[kind]?.[id];
  if (!layer) throw new Error(`Missing ${kind} layer: ${id}`);

  if (!layer.extends) return deepClone(layer);

  const parent = resolveLayerInner(registry, kind, layer.extends, [...stack, id], depth + 1);
  return deepMerge(parent, layer) as SceneLayerRecord;
}

function deepMerge(base: unknown, override: unknown): unknown {
  if (Array.isArray(base) || Array.isArray(override)) {
    return deepClone(override);
  }
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return deepClone(override);
  }

  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (key === 'extends') continue;
    out[key] = key in out ? deepMerge(out[key], value) : deepClone(value);
  }
  return out;
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
